import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { es } from "date-fns/locale";
import "./date-range-picker.css";
import createLogScale from "@visx/scale/lib/scales/log";

const customEs = {
    ...es,
    localize: {
        ...es.localize,
        day: (n) => ["D", "L", "M", "X", "J", "V", "S"][n],
        month: (n, opts) => {
            const original = es.localize.month(n, opts);
            return original.charAt(0).toUpperCase() + original.slice(1);
        },
    },
};

const formatDate = (date) => {
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
};

const parseDate = (str) => {
    const [day, month, year] = str.split("-").map(Number);
    return new Date(year, month - 1, day);
};

const DateRangePicker = ({
    show,
    handleClose,
    onApply,
    inline = false,
    currentRange,
}) => {
    const initialRange = useRef({
        startDate: new Date(),
        endDate: new Date(),
        key: "selection",
    });

    const [selectionRange, setSelectionRange] = useState(initialRange.current);
    const [hasUserInteracted, setHasUserInteracted] = useState(false);
    const [hasInitialRange, setHasInitialRange] = useState(false);

    useEffect(() => {
        if (!currentRange) {
            setSelectionRange(initialRange.current);
            setHasInitialRange(false);
            setHasUserInteracted(false);
            return;
        }

        let startDate, endDate;

        if (typeof currentRange === "string" && currentRange.includes("–")) {
            const [startStr, endStr] = currentRange.split(" – ").map(s => s.trim());
            startDate = parseDate(startStr);
            endDate = parseDate(endStr);
        }
        else if (typeof currentRange === "object" && currentRange.start && currentRange.end) {
            startDate = parseDate(currentRange.start);
            endDate = parseDate(currentRange.end);
        }

        if (startDate && endDate && !isNaN(startDate) && !isNaN(endDate)) {
            setSelectionRange({
                startDate,
                endDate,
                key: "selection",
            });
            setHasInitialRange(true);
        } else {
            setSelectionRange(initialRange.current);
            setHasInitialRange(false);
        }

        setHasUserInteracted(false);
    }, [currentRange, show]);

    const handleSelect = (ranges) => {
        setSelectionRange(ranges.selection);
        setHasUserInteracted(true);
    };

    const handleApply = () => {
        if (onApply) {
            onApply({
                start: formatDate(selectionRange.startDate),
                end: formatDate(selectionRange.endDate),
            });
        }
        if (!inline) handleClose();
    };

    const handleClear = () => {
        if (onApply) onApply(null);
        handleClose();
    };

    const isModified =
        hasUserInteracted &&
        (selectionRange.startDate.toDateString() !==
            initialRange.current.startDate.toDateString() ||
            selectionRange.endDate.toDateString() !==
            initialRange.current.endDate.toDateString() ||
            hasUserInteracted);

    const showClearButton = hasInitialRange || isModified;

    const picker = (
        <DateRange
            editableDateInputs
            onChange={handleSelect}
            moveRangeOnFirstSelection={false}
            ranges={[selectionRange]}
            months={2}
            direction="horizontal"
            locale={customEs}
            showDateDisplay={false}
            className="dateRangePicker"
        />
    );

    return inline ? (
        picker
    ) : (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            className="date-range-picker-modal"
        >
            {picker}
            <Modal.Footer className="p-0 border-0">
                <Button variant="btn btn-light" onClick={handleClose}>
                    Cancelar
                </Button>

                {showClearButton && (
                    <Button variant="btn btn-light" onClick={handleClear}>
                        Limpiar
                    </Button>
                )}

                <Button variant="btn btn-light" onClick={handleApply}>
                    Ok
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default DateRangePicker;
